import { z } from "zod/v4"

import type { AiChatFeedbackSentiment } from "./chat-feedback"
import type { ConnectorAttribution } from "./connector-attribution"

export const AI_CITATION_TYPE_DOCS = "docs"
export const AI_CITATION_TYPE_CHANGELOG = "changelog"
export const AI_CITATION_TYPE_ROADMAP = "roadmap"

export const AI_ACTION_TYPE_SUPPORT_REQUEST = "support-request"

// Sent after every widget answer: whether the customer asked for a person. The
// widget holds the handoff button until it knows, then hands off or shows it.
export const AI_CHAT_HANDOFF_CHECK_PART = "data-handoff-check"
export const aiChatHandoffCheckSchema = z.object({
	customerAskedForHuman: z.boolean(),
})

// The agent ran out of resolutions. The widget renders its own localized line
// for this, so the refusal travels as a code and never as our billing wording.
export const AI_AGENT_PAUSED_ERROR_CODE = "ai_agent_paused"

// What the model learns about an image in a search result. Coordinates stay on
// the server, so an annotation can never land where the model guessed.
export interface ToolSearchResultImage {
	ref: string
	description: string
	regions: string[]
}

export interface ToolSearchResult {
	title: string
	content: string
	id: string
	icon: string | null
	// "link" rows carry no body; the model reads the page at `url`.
	kind?: "doc" | "link"
	url?: string
	images?: ToolSearchResultImage[]
}

export const AI_IMAGE_ANNOTATION_TYPES = [
	"arrow",
	"arrow-step",
	"circle",
] as const

export const aiImageAnnotationTypeSchema = z.enum(AI_IMAGE_ANNOTATION_TYPES)
export type AiImageAnnotationType = z.infer<typeof aiImageAnnotationTypeSchema>

// Box of the region the mark points at, normalized to 0-1 against the image.
export const aiImageAnnotationSchema = z.object({
	type: aiImageAnnotationTypeSchema,
	x: z.number(),
	y: z.number(),
	w: z.number(),
	h: z.number(),
	step: z.number().optional(),
	label: z.string().optional(),
})
export type AiImageAnnotation = z.infer<typeof aiImageAnnotationSchema>

export const aiImageSpecSchema = z.object({
	id: z.string(),
	src: z.string(),
	alt: z.string(),
	width: z.number(),
	height: z.number(),
	caption: z.string().optional(),
	annotations: z.array(aiImageAnnotationSchema),
})
export type AiImageSpec = z.infer<typeof aiImageSpecSchema>

export const AI_IMAGE_PLACEHOLDER_TAG = "pl-image"

export const aiImagePlaceholder = (id: string): string =>
	`<${AI_IMAGE_PLACEHOLDER_TAG} data-id="${id}"></${AI_IMAGE_PLACEHOLDER_TAG}>`

export const aiCitationTypeSchema = z
	.enum([
		AI_CITATION_TYPE_DOCS,
		AI_CITATION_TYPE_CHANGELOG,
		AI_CITATION_TYPE_ROADMAP,
	])
	.describe("the type of the source")
export type AICitationType = z.infer<typeof aiCitationTypeSchema>

export const aiActionTypeSchema = z
	.enum([AI_ACTION_TYPE_SUPPORT_REQUEST])
	.describe("the action to add")
export type AIActionType = z.infer<typeof aiActionTypeSchema>

export const aiCiteItemSchema = z.object({
	type: aiCitationTypeSchema,
	icon: z.string().optional().describe("the lucid icon of the source"),
	id: z.string().describe("the id of the source"),
	title: z.string().describe("the title of the source"),
	// Where the chip opens, for a source that lives outside Productlane. Filled
	// in from the database on the way out; anything the model puts here is
	// dropped, so it can never point a customer at an address of its own.
	url: z.string().optional(),
})
export type AICiteItem = z.infer<typeof aiCiteItemSchema>

// Sources the AI consulted while drafting a customer reply, surfaced in the
// composer "Drafted by AI" tooltip. Persisted as JSON on `Feedback.aiDraftSources`.
export const aiDraftSourceSchema = z.object({
	type: z.enum([
		"docs",
		"changelog",
		"roadmap",
		"thread",
		"github",
		"postgres",
		"connector",
	]),
	// Resource identifier inside Productlane (article id, project id, thread
	// id, …). Used by the composer tooltip to build internal navigation URLs.
	// Optional because legacy rows persisted before this field was added
	// won't have it, and because some types (e.g. postgres query name) don't
	// have a routable id.
	id: z.string().optional(),
	title: z.string(),
	identifier: z.string().optional(),
	// Pre-computed external URL — currently only set for GitHub PRs.
	url: z.string().optional(),
})
export type AiDraftSource = z.infer<typeof aiDraftSourceSchema>

export const aiCiteToolInputSchema = z.object({
	sources: z.array(aiCiteItemSchema),
})
export type AICiteToolInput = z.infer<typeof aiCiteToolInputSchema>

export const aiActionInputSchema = z.object({
	action: aiActionTypeSchema,
	promisedFollowUp: z
		.boolean()
		.optional()
		.describe(
			"true when this reply tells the customer that a person, the team, or support will follow up, look into it, or take it over. false when you are only offering the button.",
		),
})
export type AIActionToolInput = z.infer<typeof aiActionInputSchema>

export const aiResolutionToolInputSchema = z.object({
	confidence: z
		.number()
		.min(0)
		.max(1)
		.describe("the confidence in the resolution"),
})
export type AIResolutionToolInput = z.infer<typeof aiResolutionToolInputSchema>

export const aiMarkResolvedToolInputSchema = z.object({
	customerQuote: z
		.string()
		.min(1)
		.max(300)
		.describe(
			"the exact words from the customer's latest message that say the problem is solved, copied verbatim",
		),
})
export type AIMarkResolvedToolInput = z.infer<
	typeof aiMarkResolvedToolInputSchema
>

// The grounds a turn may stay silent on. "no-help" covers email and Slack: a reply that
// only acknowledges buries the teammate answer the person actually needs.
export const AI_SKIP_REPLY_GROUNDS = ["automated", "bulk", "no-help"] as const
export type AiSkipReplyGround = (typeof AI_SKIP_REPLY_GROUNDS)[number]

export const aiSkipReplyToolInputSchema = z.object({
	ground: z
		.enum(AI_SKIP_REPLY_GROUNDS)
		.describe(
			"automated: a machine wrote it (auto-reply, bounce, out-of-office, system notice). bulk: a newsletter, marketing blast, or mailing list. no-help: a real question you cannot help with, so all you could send is an acknowledgement.",
		),
	detail: z
		.string()
		.max(200)
		.describe(
			"one short phrase naming what gave it away, or what you could not answer",
		),
})
export type AiSkipReplyToolInput = z.infer<typeof aiSkipReplyToolInputSchema>

// Workspace entities a user pinned to a copilot turn with "@". The server
// resolves each id back to real content; the label is only for display.
export const AI_CONTEXT_ATTACHMENT_TYPES = [
	"issue",
	"project",
	"thread",
	"contact",
	"company",
	"article",
	"changelog",
	"user",
] as const

export const aiContextAttachmentSchema = z.object({
	type: z.enum(AI_CONTEXT_ATTACHMENT_TYPES),
	id: z.string().min(1).max(64),
	label: z.string().min(1).max(200),
	// Identifier shown ahead of the label, so the transcript can rebuild the
	// same badge the composer showed.
	prefix: z.string().max(120).nullish(),
})
export type AiContextAttachmentType =
	(typeof AI_CONTEXT_ATTACHMENT_TYPES)[number]
export type AiContextAttachment = z.infer<typeof aiContextAttachmentSchema>

export const AI_CONTEXT_ATTACHMENT_LIMIT = 10

export interface AiChatMessageAttachment {
	url: string
	name: string
	type: string // MIME type e.g. "image/png", "application/pdf", "text/csv"
}

// Which surface the copilot is bound to. `workspace` is the thread-less
// inbox copilot; `article`/`changelog` are the docs and changelog editors;
// `reporting` is the agent-performance copilot on the reporting AI tab;
// `agent` edits the recommendation open on the Agent page.
export type CopilotScope =
	| { kind: "thread"; threadId: string; threadTitle?: string }
	| { kind: "article"; articleId: string }
	| { kind: "changelog"; changelogId: string }
	| { kind: "workspace" }
	| { kind: "reporting" }
	// One idea, or one chat started with "+", on the Agent page; without
	// either it is the page-wide copilot.
	| { kind: "agent"; recommendationId?: string; chatId?: string }

export type ContentRewriteTarget = "composer" | "article" | "changelog"

export type AgentSetupActionStatus =
	| "pending"
	| "executing"
	| "executed"
	| "rejected"
	| "errored"

// What an assistant chat session stores about its surface, so the Agent
// sidebar can list and reopen it.
export function copilotScopeIdentity(scope: CopilotScope): {
	scopeKind: CopilotScope["kind"] | "agent-idea"
	scopeId: string | null
} {
	switch (scope.kind) {
		case "thread":
			return { scopeKind: "thread", scopeId: scope.threadId }
		case "article":
			return { scopeKind: "article", scopeId: scope.articleId }
		case "changelog":
			return { scopeKind: "changelog", scopeId: scope.changelogId }
		case "agent":
			// An idea's chat reopens with its card, so it needs its own kind.
			return scope.recommendationId
				? { scopeKind: "agent-idea", scopeId: scope.recommendationId }
				: { scopeKind: "agent", scopeId: scope.chatId ?? null }
		default:
			return { scopeKind: scope.kind, scopeId: null }
	}
}

export const CHAT_TITLE_MAX = 80

// The sidebar names a chat after its first message, cut on a word boundary.
export function chatTitleFromMessage(text: string): string | null {
	const flat = text.replace(/\s+/g, " ").trim()
	if (!flat) return null
	if (flat.length <= CHAT_TITLE_MAX) return flat
	const cut = flat.slice(0, CHAT_TITLE_MAX)
	const lastSpace = cut.lastIndexOf(" ")
	return (lastSpace > CHAT_TITLE_MAX / 2 ? cut.slice(0, lastSpace) : cut).trim()
}

export function copilotScopeKey(scope: CopilotScope): string {
	switch (scope.kind) {
		case "thread":
			return `thread:${scope.threadId}`
		case "article":
			return `article:${scope.articleId}`
		case "changelog":
			return `changelog:${scope.changelogId}`
		case "workspace":
			return "workspace"
		case "reporting":
			return "reporting"
		case "agent":
			if (scope.recommendationId) return `agent:${scope.recommendationId}`
			if (scope.chatId) return `agent:chat:${scope.chatId}`
			return "agent"
	}
}

// The editor a rewrite applies to, given the copilot's scope. Workspace scope
// has no editor target, so it never produces a content rewrite.
export function contentRewriteTargetForScope(
	scope: CopilotScope,
): ContentRewriteTarget | null {
	switch (scope.kind) {
		case "thread":
			return "composer"
		case "article":
			return "article"
		case "changelog":
			return "changelog"
		case "workspace":
		case "reporting":
		case "agent":
			return null
	}
}

export interface AiChatMessage {
	id: string
	role: "user" | "assistant"
	content: string
	processed: boolean
	suggestions?: string[]
	actions: AIActionToolInput[]
	sources: AICiteItem[]
	waitAction?: string
	// Set when the copilot was blocked by the AI spend limit, so the UI can
	// render a button to the billing/usage page instead of a plain error.
	capReached?: boolean
	// Set when the stream died mid-answer; content keeps whatever streamed.
	errored?: boolean
	// Marks the moment the conversation left the agent for the team. Rendered as
	// a divider rather than an assistant bubble, so the customer can see where
	// the AI stopped answering.
	handoff?: boolean
	// The server has read whether the customer asked for a person this turn. If
	// they did, the chat goes to the team without a "Talk with a human" button.
	handoffChecked?: boolean
	customerAskedForHuman?: boolean
	// Workspace entities the user pinned to this turn with "@".
	attachedContext?: AiContextAttachment[]
	// The agent needs the visitor's address before it can file their request, so
	// the widget opens the email box under this answer.
	emailRequested?: boolean
	// Progress on the Linear issue this conversation is linked to, and on the pull
	// requests fixing it. Rendered as its own row; only the identifier is public.
	issueProgress?: {
		kind: "planned" | "started" | "in_review" | "merged" | "shipped"
		issueIdentifier: string
	}
	createdAt: Date
	// A team member's rating of this answer, kept so the filled icon survives
	// a reload.
	feedback?: { sentiment: AiChatFeedbackSentiment }
	liveChatMessageId?: string // Special identifier for AI messages created for live chat
	// On a customer-facing surface title and description are only ever the words
	// the agent wrote in this conversation, flagged by agentAuthored. Linear's
	// own text stays out: one issue carries several customers' reports.
	linearIssueCreated?: {
		id: string
		identifier: string
		title?: string
		description?: string
		important?: boolean
		agentAuthored?: boolean
	}
	linearIssueEdited?: {
		id: string
		identifier: string
		title?: string
		description?: string
		important?: boolean
		agentAuthored?: boolean
	}
	linearIssueLinked?: {
		id: string
		identifier: string
		title?: string
		description?: string
		important?: boolean
		status?: string
		type: "issue" | "project"
		linearId: string
	}
	linearIssueRemoved?: { identifier?: string; title?: string }
	// Unified content suggestion rendered as an "Apply" preview card. `target`
	// picks which editor Apply replaces: the thread reply composer, a help
	// center article body, or a changelog body.
	contentRewrite?: {
		target: ContentRewriteTarget
		html: string
		description?: string
		title?: string
	}
	// A docs-proposal run the copilot started. `proposalGroupId` is the run's
	// own id; the group a reviewer opens can differ, so `resolvedGroupId` is
	// filled in from the run's outcome. `prompt` and the "pending" status only
	// appear on messages from before the tool ran the agent itself.
	docsProposalSuggestion?: {
		summary: string
		issueIds: string[]
		issueIdentifiers?: string[]
		prUrls?: string[]
		prompt?: string
		status?: "pending" | "generating" | "ready" | "skipped" | "error"
		proposalGroupId?: string
		resolvedGroupId?: string
		// When the run's group row landed, so the card can say how long it took
		// even after a reload.
		finishedAt?: number
		// The agent's own one-sentence account of what it changed.
		rationale?: string
		changes?: {
			id: string
			kind: "CREATE" | "EDIT" | "DELETE"
			title: string
			route: string
		}[]
		errorMessage?: string
	}
	continueInClaude?: {
		summary: string
	}
	// The Agent's to-do list, rendered live from the open ideas when shown.
	agentTodos?: {
		shownAt: string
	}
	stripeActionApproval?: {
		pendingActionId: string
		actionType:
			| "apply_promotion_code"
			| "remove_promotion_code"
			| "cancel_subscription"
			| "change_plan"
			| "extend_trial"
		summary: string
		// Free-form preview payload populated by the proposing tool. Rendered
		// as labelled "before → after" rows by the approval card.
		preview: Record<string, unknown>
		status: "pending" | "executing" | "executed" | "rejected" | "errored"
		// Populated after a successful execute mutation; surfaced as a
		// "Verify in Stripe" link on the card so the user can confirm the
		// change in the dashboard.
		subscriptionUrl?: string
		errorMessage?: string
	}
	// Whitelisted postgres queries that fed this assistant turn. Populated
	// automatically by the runPostgresWhitelistedQuery tool result so the UI
	// can attribute the answer to the saved queries that produced it. Empty
	// for one-off proposed queries since those have no reusable identity.
	usedQueries?: { name: string }[]
	// Connector tools this assistant turn called, derived from the attribution
	// each connector tool result carries.
	usedConnectors?: ConnectorAttribution[]
	postgresActionApproval?: {
		pendingActionId: string
		sql: string
		reason?: string | null
		summary: string
		status: "pending" | "executing" | "executed" | "rejected" | "errored"
		// Optional: AI-supplied parameterized version that can be saved as a
		// whitelisted query in one click.
		whitelistSuggestion?: {
			name: string
			description: string
			sql: string
			parameters: {
				name: string
				type: "string" | "number" | "boolean" | "timestamp"
			}[]
		} | null
		// Populated after a successful execute. Surfaced as a tiny preview of
		// the result on the card.
		result?: {
			rowCount: number
			truncated: boolean
			columns: string[]
			rows: Record<string, unknown>[]
			durationMs: number
		}
		// Set after a successful "Run and save" so the card can confirm the
		// new whitelist entry was created.
		savedWhitelistName?: string | null
		errorMessage?: string
	}
	// Approval cards from the setup guide. Persisted with their status, so a
	// reload shows the outcome instead of a live button.
	linearIssueProposal?: {
		pendingActionId: string
		title: string
		description: string
		teamName?: string | null
		status: AgentSetupActionStatus
		created?: { identifier: string; url: string }
		errorMessage?: string
	}
	agentInstructionsProposal?: {
		pendingActionId: string
		before: string | null
		after: string
		reason: string
		status: AgentSetupActionStatus
		errorMessage?: string
	}
	attachments?: AiChatMessageAttachment[]
	// Docs images this turn resolved through showDocsImage, keyed by the id in
	// the <pl-image> placeholder the answer body carries.
	images?: Record<string, AiImageSpec>
}
